import { useCallback, useEffect, useMemo, useState } from "react";
import api, { formatApiErrorDetail } from "../../api";
import { OWNER_LABEL, checkAnchorId } from "./labels";
import PlaybookCard from "./PlaybookCard";
import { filterCards, matchWhere } from "./search";

// The owner filter, in the order Ward reads it: his own first.
const LANES = [
  { key: "", label: "All" },
  { key: "ward", label: OWNER_LABEL.ward },
  { key: "marco", label: OWNER_LABEL.marco },
];

export default function Playbook({ isAdmin, onOpenCapture, focus, onFocusHandled }) {
  const [cards, setCards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  // A set, not a single slug: the accordion used to close one card to open
  // another, so every expand was also a collapse somewhere above it — and on
  // a phone the header just tapped could end up off screen. Cards now open
  // and close independently.
  const [openSlugs, setOpenSlugs] = useState(() => new Set());
  // The check to scroll to once the card carrying it has expanded.
  const [pendingCheck, setPendingCheck] = useState(null);
  // Deliberately not persisted. A filter that survives a reload is a Playbook
  // that has silently lost four cards since the last time you looked at it.
  const [q, setQ] = useState("");
  const [lane, setLane] = useState("");

  const load = useCallback(() => {
    setLoading(true);
    return api.get("/marketing/playbook")
      .then(({ data }) => { setCards(data.items); setError(""); })
      .catch((e) => setError(formatApiErrorDetail(e?.response?.data?.detail)))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const shown = useMemo(() => filterCards(cards, { q, owner: lane }), [cards, q, lane]);

  // Arriving from the drawer's approve checklist: open the card the check
  // lives in, then scroll to that check's line. Two effects, not one: the
  // anchor does not exist in the DOM until the expanded body has committed,
  // so the scroll has to wait for the render that opening the card causes.
  useEffect(() => {
    if (!focus || loading) return;
    // Clear the filter first: arriving from the drawer means a specific card
    // has to be on screen, and a filter it does not match would hide it.
    setQ("");
    setLane("");
    setOpenSlugs((prev) => new Set(prev).add(focus.slug));
    setPendingCheck(focus.checkId || null);
    onFocusHandled?.();
  }, [focus, loading, onFocusHandled]);

  useEffect(() => {
    if (!pendingCheck) return;
    // Missing anchor is fine — the body is Marco-editable, and an edit that
    // drops the `**H1 — …**` notation should cost a scroll position, not the
    // link. The right card is open either way.
    document.getElementById(checkAnchorId(pendingCheck))?.scrollIntoView({ block: "center" });
    setPendingCheck(null);
  }, [pendingCheck, openSlugs]);

  // Deliberately unguarded: a failure has to reach PlaybookCard, which is the
  // only caller. It keeps the editor open with the draft intact and renders
  // the message beside the textarea the edit was typed into — a banner at the
  // top of a seven-card list is off-screen from a 20-row body field. Before
  // this, the rejection escaped the async event handler entirely: no message,
  // and an 11 KB body reverting on the next reload with nothing said.
  const save = async (slug, patch) => {
    await api.put(`/marketing/playbook/${slug}`, patch);
    await load();
  };

  if (loading) return <div className="disc-empty">Loading…</div>;
  if (error) return <div className="mk-error" data-testid="mk-playbook-error">{error}</div>;
  if (!cards.length) return <div className="disc-empty">No playbook cards yet.</div>;

  const hidden = cards.length - shown.length;

  return (
    <div data-testid="mk-playbook">
      <div className="mk-find">
        <div className="mk-find-row">
          <input
            className="mk-input mk-find-input"
            type="search"
            placeholder="Search the playbook — footage, permissions, the One Question…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            aria-label="Search the playbook"
            data-testid="mk-playbook-search"
          />
          {(q || lane) && (
            <button type="button" className="mk-btn-quiet"
              onClick={() => { setQ(""); setLane(""); }}
              data-testid="mk-playbook-clear">Clear</button>
          )}
        </div>
        <div className="mk-lanes" role="group" aria-label="Whose cards">
          {LANES.map((l) => (
            <button
              key={l.key || "all"}
              type="button"
              className={`mk-btn-quiet ${lane === l.key ? "on" : ""}`}
              aria-pressed={lane === l.key}
              onClick={() => setLane(l.key)}
              data-testid={`mk-lane-${l.key || "all"}`}
            >
              {l.label}
            </button>
          ))}
        </div>
        {/* Says what the filter did. Seven cards going to two silently is
            indistinguishable from five cards having been deleted. */}
        {hidden > 0 && (
          <div className="mk-find-count" data-testid="mk-playbook-count">
            Showing {shown.length} of {cards.length} cards.
          </div>
        )}
      </div>

      {!shown.length && (
        <div className="disc-empty" data-testid="mk-playbook-none">
          Nothing here matches “{q}”. The words are searched inside the cards
          too, so a card that does not mention it will not appear.
        </div>
      )}

      <div className="mk-cards">
        {shown.map((c) => (
          <PlaybookCard
            key={c.slug}
            card={c}
            isAdmin={isAdmin}
            expanded={openSlugs.has(c.slug)}
            // Where the words were found. A card matched on its body alone
            // shows nothing on its head that contains them, which reads as a
            // wrong result until it says so.
            matchedInBody={matchWhere(c, q) === "body"}
            onToggle={() => setOpenSlugs((prev) => {
              const next = new Set(prev);
              if (!next.delete(c.slug)) next.add(c.slug);
              return next;
            })}
            onSave={save}
            onOpenCapture={onOpenCapture}
          />
        ))}
      </div>
    </div>
  );
}
